/**
 * msal.js — Microsoft Graph + Google Auth for C.H.A.R.L.I.E.
 *
 * Microsoft: PKCE Authorization Code Flow via popup.
 *   - Generates code_verifier / code_challenge client-side
 *   - Popup redirects to /auth-popup.html which posts back the auth code
 *   - Main window exchanges code for access_token + refresh_token via
 *     the /api/ms-token Vercel serverless function (keeps client_secret server-side)
 *   - Tokens stored in Supabase auth_tokens table
 *   - Auto-refresh on every getMsToken call when within 5 min of expiry
 *
 * Google: GIS implicit flow (unchanged — no client_secret needed for Gmail).
 */

import {
  storeMsTokenInSupabase,
  storeGoogleTokenInSupabase,
  fetchAccessToken,
  lastStoreTokenError,
} from './supabaseAuth';
import { ensureParagraphSpacing } from './paragraphSpacing';

// ─── CONSTANTS ────────────────────────────────────────────────────────────────

const MSAL_CLIENT_ID  = '46d5f003-1e9c-4670-a92b-2ff216e919a6';
const MS_TENANT       = 'common'; // multi-account: works for any M365 tenant
const POPUP_REDIRECT  = window.location.origin + '/auth-popup.html';
const GRAPH_SCOPES    = 'https://graph.microsoft.com/Mail.Send https://graph.microsoft.com/Mail.Send.Shared https://graph.microsoft.com/Mail.ReadWrite https://graph.microsoft.com/Mail.ReadWrite.Shared https://graph.microsoft.com/User.Read https://graph.microsoft.com/Calendars.ReadWrite offline_access';
// TOKEN_ENDPOINT is handled server-side in /api/ms-token.js

// ─── PKCE HELPERS ─────────────────────────────────────────────────────────────

function generateVerifier() {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return btoa(String.fromCharCode(...array))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

async function generateChallenge(verifier) {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

// ─── MICROSOFT LOGIN (PKCE popup) ─────────────────────────────────────────────

/**
 * Open a popup, run PKCE auth code flow, exchange code for tokens,
 * store access_token + refresh_token in Supabase.
 * Returns the user's email (UPN) or null.
 */
export async function msLoginRedirect(loginHint, partnerId) {
  const verifier   = generateVerifier();
  const challenge  = await generateChallenge(verifier);
  const state      = Math.random().toString(36).substring(2);

  const authUrl =
    `https://login.microsoftonline.com/${MS_TENANT}/oauth2/v2.0/authorize?` +
    `client_id=${MSAL_CLIENT_ID}` +
    `&response_type=code` +
    `&redirect_uri=${encodeURIComponent(POPUP_REDIRECT)}` +
    `&response_mode=query` +
    `&scope=${encodeURIComponent(GRAPH_SCOPES)}` +
    `&code_challenge=${challenge}` +
    `&code_challenge_method=S256` +
    `&state=${state}` +
    `&prompt=select_account` +
    (loginHint ? `&login_hint=${encodeURIComponent(loginHint)}` : '');

  return new Promise((resolve, reject) => {
    let settled = false;

    async function handleMessage(event) {
      if (event.origin !== window.location.origin) return;
      if (settled) return;

      const { type, code, error, errorDescription } = event.data || {};

      if (type === 'msal-popup-error') {
        settled = true;
        cleanup();
        return reject(new Error(errorDescription || error || 'Auth error'));
      }

      if (type === 'msal-popup-code' && code) {
        settled = true;
        cleanup();
        try {
          // Exchange code for tokens directly from browser (SPA PKCE public client).
          // No client_secret needed — code_verifier proves authenticity.
          const tokenBody = new URLSearchParams({
            client_id:     MSAL_CLIENT_ID,
            code,
            redirect_uri:  POPUP_REDIRECT,
            grant_type:    'authorization_code',
            code_verifier: verifier,
            scope:         GRAPH_SCOPES,
          });

          const res = await fetch(
            `https://login.microsoftonline.com/${MS_TENANT}/oauth2/v2.0/token`,
            {
              method:  'POST',
              headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
              body:    tokenBody,
            }
          );

          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            return reject(new Error(err.error_description || 'Token exchange failed'));
          }

          const tokens = await res.json();
          // tokens: { access_token, refresh_token, expires_in, id_token, ... }

          if (partnerId) {
            const saved = await storeMsTokenInSupabase(partnerId, {
              accessToken:  tokens.access_token,
              refreshToken: tokens.refresh_token,
              expiresIn:    tokens.expires_in,
              idToken:      tokens.id_token,
            });
            // Signed in to Microsoft is not the same as connected: the connection counts only once it is saved.
            if (!saved) return reject(new Error(`Signed in to Microsoft, but the connection could not be saved${lastStoreTokenError ? `: ${lastStoreTokenError}` : '.'}`));
          }

          // Extract email from id_token payload (JWT middle segment)
          let email = loginHint || null;
          try {
            const payload = JSON.parse(atob(tokens.id_token.split('.')[1]));
            email = payload.preferred_username || payload.upn || payload.email || loginHint;
          } catch {}

          resolve(email);
        } catch (err) {
          reject(err);
        }
      }

      // Legacy implicit flow fallback
      if (type === 'msal-popup-complete' && event.data.accessToken) {
        settled = true;
        cleanup();
        if (partnerId) {
          await storeMsTokenInSupabase(partnerId, {
            accessToken:  event.data.accessToken,
            refreshToken: null,
            expiresIn:    3600,
            idToken:      null,
          });
        }
        resolve(loginHint || null);
      }
    }

    function cleanup() {
      window.removeEventListener('message', handleMessage);
      clearTimeout(timeout);
    }

    window.addEventListener('message', handleMessage);

    window.open(authUrl, 'msauth', 'width=500,height=640,left=300,top=100');

    const timeout = setTimeout(() => {
      if (!settled) {
        settled = true;
        cleanup();
        reject(new Error('Login timed out after 5 minutes'));
      }
    }, 5 * 60 * 1000);
  });
}

// ─── GET VALID MS TOKEN ────────────────────────────────────────────────────────

/**
 * Returns a valid access token for the given partner.
 * Auto-refreshes from Supabase if within 5 min of expiry.
 * Returns null if not connected (caller should prompt re-auth).
 */
export async function getMsToken(email, partnerId) {
  // The server holds the saved sign-in and refreshes it; the browser only ever receives a short-lived access token (null = needs signing in again).
  return fetchAccessToken(partnerId, 'microsoft');
}

// ─── GRAPH SEND ───────────────────────────────────────────────────────────────

export async function sendGraphEmail({ token, from, to, cc, bcc, subject, body, listUnsubscribe, attachments }) {
  // Accept comma-separated lists for to/cc/bcc; build Graph recipient arrays.
  const toRecipients = String(to || '').split(',').map((s) => s.trim()).filter(Boolean).map((address) => ({ emailAddress: { address } }));
  const ccRecipients = String(cc || '').split(',').map((s) => s.trim()).filter(Boolean).map((address) => ({ emailAddress: { address } }));
  const bccRecipients = String(bcc || '').split(',').map((s) => s.trim()).filter(Boolean).map((address) => ({ emailAddress: { address } }));
  const message = {
    subject,
    body: { contentType: 'HTML', content: ensureParagraphSpacing(body) },
    toRecipients,
    from: { emailAddress: { address: from } },
  };
  if (ccRecipients.length) message.ccRecipients = ccRecipients;
  if (bccRecipients.length) message.bccRecipients = bccRecipients;
  // v1.7.24 — attachments are NOT inlined here; they're added to the draft after create (below).
  // v1.5.13: RFC 8058 List-Unsubscribe headers for one-click unsubscribe in Outlook/Gmail
  if (listUnsubscribe) {
    message.internetMessageHeaders = [
      { name: 'List-Unsubscribe', value: listUnsubscribe },
      { name: 'List-Unsubscribe-Post', value: 'List-Unsubscribe=One-Click' },
    ];
  }
  // v1.7.23.g — Send via /me but with `from` set to the shared mailbox. Our v1.7.23.c attempt
  // to POST to /users/{shared}/messages returned "Access is denied": a DELEGATED Mail.Send token
  // is NOT authorized to operate on another user's mailbox endpoint, even when Exchange Send-As
  // is granted. The pattern that DOES work with delegated token + Send-As: create the draft in
  // /me, set message.from = the shared mailbox, and Exchange honors the Send-As on send — the
  // mail goes out AS Phoenix and lands in Phoenix's Sent. (Shane confirmed he can send-as these
  // mailboxes manually in Outlook, i.e. the Exchange permission exists.) `sender` is also set so
  // Graph doesn't fall back to the token owner.
  if (from) message.sender = { emailAddress: { address: from } };
  const mailboxBase = 'https://graph.microsoft.com/v1.0/me';

  // v1.5.14: use createMessage + send flow so we can capture conversationId for reply detection
  // v1.7.41.72 (Ledger #1290) -- $select forces internetMessageId into
  // the create response. Without it Graph returns only the DRAFT id,
  // which becomes invalid the moment the message is sent -- "dead on
  // arrival by design", as api/send-hello.js already documented after
  // hitting this same wall.
  //
  // internetMessageId is the RFC 5322 Message-ID. It is the value that
  // appears in the In-Reply-To and References headers of any bounce or
  // reply, so it is the ONLY thing worth correlating on. 219 of 235
  // sends were untrackable because this was never captured on the
  // client path -- the server cron (api/sequence-cron.js) was fixed for
  // this in July, but the browser sender never was.
  const createRes = await fetch(`${mailboxBase}/messages?$select=id,internetMessageId`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(message),
  });
  if (!createRes.ok) {
    const err = await createRes.json().catch(() => ({}));
    throw new Error(err.error?.message || `Graph create failed (${createRes.status})`);
  }
  const created = await createRes.json();
  const messageId = created.id;
  const conversationId = created.conversationId || null;
  // let, not const -- a fallback read below fills this when $select
  // does not return it on create.
  let internetMessageId = created.internetMessageId || null;

  // v1.7.24 — Attachments are added to the DRAFT separately (more reliable than inline in the
  // create body for larger files). Then we send. This fixes quotes that logged as sent but
  // never left because an inline attachment stalled the create/send.
  if (Array.isArray(attachments) && attachments.length) {
    for (const a of attachments) {
      const attRes = await fetch(`${mailboxBase}/messages/${messageId}/attachments`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          '@odata.type': '#microsoft.graph.fileAttachment',
          name: a.name,
          contentType: a.contentType || 'application/octet-stream',
          contentBytes: a.contentBytes,
        }),
      });
      if (!attRes.ok) {
        const err = await attRes.json().catch(() => ({}));
        throw new Error(err.error?.message || `Graph attachment failed (${attRes.status})`);
      }
    }
  }

  // Read it back BEFORE sending -- once sent, the draft id no longer
  // resolves and this becomes unrecoverable.
  if (!internetMessageId && messageId) {
    try {
      const readRes = await fetch(`${mailboxBase}/messages/${messageId}?$select=internetMessageId`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (readRes.ok) {
        const detail = await readRes.json();
        internetMessageId = detail.internetMessageId || null;
      }
    } catch (_e) {
      // Never block a send over tracking. A message that goes out
      // untracked is far better than one that does not go out.
    }
  }

  const sendRes = await fetch(`${mailboxBase}/messages/${messageId}/send`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!sendRes.ok && sendRes.status !== 202) {
    const err = await sendRes.json().catch(() => ({}));
    throw new Error(err.error?.message || `Graph send failed (${sendRes.status})`);
  }
  return { messageId, internetMessageId, threadId: conversationId };
}

// ─── LEGACY STUBS (keep for AppContext compatibility) ──────────────────────────

export const msalInstance = { getAllAccounts: () => [], initialize: async () => {} };
export async function handleMsRedirect() { return null; }
export function getMsAccounts() { return []; }

// ─── GOOGLE AUTH (unchanged) ──────────────────────────────────────────────────

const GOOGLE_CLIENT_ID   = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const GMAIL_SCOPES       = 'openid email profile https://www.googleapis.com/auth/gmail.send https://www.googleapis.com/auth/gmail.readonly https://www.googleapis.com/auth/calendar https://www.googleapis.com/auth/calendar.events';
const GOOGLE_TOKEN_KEY_PREFIX = 'charlie_google_token_';

export function storeGoogleToken(email, tokenData) {
  localStorage.setItem(
    GOOGLE_TOKEN_KEY_PREFIX + email.toLowerCase(),
    JSON.stringify({ ...tokenData, stored_at: Date.now() })
  );
}

export function getStoredGoogleToken(email) {
  if (!email) return null;
  const raw = localStorage.getItem(GOOGLE_TOKEN_KEY_PREFIX + email.toLowerCase());
  if (!raw) return null;
  const data = JSON.parse(raw);
  if (Date.now() - data.stored_at > 55 * 60 * 1000) {
    localStorage.removeItem(GOOGLE_TOKEN_KEY_PREFIX + email.toLowerCase());
    return null;
  }
  return data;
}

function loadGis() {
  return new Promise((resolve) => {
    if (window.google?.accounts) return resolve();
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.onload = () => resolve();
    document.head.appendChild(script);
  });
}

export async function googleLogin(email, partnerId) {
  await loadGis();
  return new Promise((resolve, reject) => {
    const client = window.google.accounts.oauth2.initTokenClient({
      client_id: GOOGLE_CLIENT_ID,
      scope: GMAIL_SCOPES,
      hint: email || undefined,
      callback: async (response) => {
        if (response.error) return reject(new Error(response.error));
        storeGoogleToken(email, response);
        if (partnerId) {
          const saved = await storeGoogleTokenInSupabase(partnerId, email, response);
          if (!saved) return reject(new Error(`Signed in to Google, but nothing was saved for ${email || 'this mailbox'}, so it is still not connected.${lastStoreTokenError ? ' ' + lastStoreTokenError : ''}`));
        }
        resolve(response.access_token);
      },
    });
    client.requestAccessToken();
  });
}

export async function sendGmailEmail({ token, from, to, cc, bcc, subject, body, listUnsubscribe }) {
  const headers = [
    `From: ${from}`,
    `To: ${to}`,
    cc ? `Cc: ${cc}` : null,
    bcc ? `Bcc: ${bcc}` : null,
    `Subject: ${subject}`,
    'MIME-Version: 1.0',
    'Content-Type: text/html; charset=utf-8',
    // v1.5.13: RFC 8058 one-click unsubscribe (Gmail requirement for bulk senders)
    listUnsubscribe ? `List-Unsubscribe: ${listUnsubscribe}` : null,
    listUnsubscribe ? 'List-Unsubscribe-Post: List-Unsubscribe=One-Click' : null,
  ].filter(Boolean).join('\r\n');
  // Use unescape+encodeURIComponent dance to safely btoa unicode
  const message = `${headers}\r\n\r\n${ensureParagraphSpacing(body)}`;
  const raw = btoa(unescape(encodeURIComponent(message)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

  const res = await fetch('https://gmail.googleapis.com/gmail/v1/users/me/messages/send', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ raw }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error?.message || 'Gmail send failed');
  }
  // v1.5.14: return threadId + messageId so we can detect replies
  const data = await res.json().catch(() => ({}));
  return { messageId: data.id || null, threadId: data.threadId || null };
}
